/**
 * Verification Voting Screen
 *
 * Doctrine: SCORING_ENGINE.md Invariant #7
 * - Community-driven lift verification
 * - No self-voting, 48h window, daily cap (20), gym-only restriction
 * - Minimum 3 votes for callus score
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  View, Text, StyleSheet, FlatList,
  TouchableOpacity, Image, Alert, Platform,
} from 'react-native';
import { VideoView, useVideoPlayer } from 'expo-video';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import SkeletonRow from '@/components/SkeletonRow';
import EmptyStateInline from '@/components/EmptyStateInline';
import {
  fetchVerifiablePosts,
  castVote,
  getRemainingVotes,
  type VerifiablePost,
  type VoteValue,
} from '@/lib/verification';
import { createLogger } from '@/lib/logger';

const log = createLogger('VERIFY');

/** Renders image or in-app video depending on URL extension */
function ProofMedia({ uri, style }: { uri: string; style: any }) {
  const isVideo = /\.(mp4|mov|webm|m4v)(\?|$)/i.test(uri);
  const player = useVideoPlayer(isVideo ? uri : null, p => {
    if (p) { p.loop = true; p.muted = false; }
  });
  if (isVideo) {
    return (
      <VideoView
        player={player}
        style={style}
        nativeControls
        allowsFullscreen
        allowsPictureInPicture={false}
      />
    );
  }
  return <Image source={{ uri }} style={style} resizeMode="cover" />;
}

export default function VerifyScreen() {
  const { theme } = useTheme();
  const { profile } = useAuth();
  const c = theme.colors;

  const [posts, setPosts] = useState<VerifiablePost[]>([]);
  const [loading, setLoading] = useState(true);
  const [remaining, setRemaining] = useState(20);
  const [votingId, setVotingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!profile?.id) return;
    setLoading(true);
    const [verifiable, votes] = await Promise.all([
      fetchVerifiablePosts(profile.id),
      getRemainingVotes(profile.id),
    ]);
    setPosts(verifiable);
    setRemaining(votes);
    setLoading(false);
  }, [profile?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleVote = useCallback(async (postId: string, vote: VoteValue) => {
    if (!profile?.id) return;
    setVotingId(postId);

    const result = await castVote(postId, profile.id, vote);
    if (result.success) {
      // Mark as voted locally
      setPosts((prev) =>
        prev.map((p) =>
          p.id === postId
            ? { ...p, user_voted: true, vote_count: p.vote_count + 1 }
            : p,
        ),
      );
      setRemaining((prev) => Math.max(0, prev - 1));
    } else {
      log.error('Vote failed:', result.error);
      const friendlyMsg = "Couldn't submit your vote. Please try again.";
      if (Platform.OS === 'web') {
        alert(friendlyMsg);
      } else {
        Alert.alert('Oops', friendlyMsg);
      }
    }
    setVotingId(null);
  }, [profile?.id]);

  const renderPost = useCallback(({ item }: { item: VerifiablePost }) => {
    const timeAgo = getTimeAgo(item.created_at);
    const isVoting = votingId === item.id;

    return (
      <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
        {/* Header */}
        <View style={styles.cardHeader}>
          <View>
            <Text style={[styles.authorName, { color: c.text }]}>
              {item.author_name}
            </Text>
            {item.author_handle && (
              <Text style={[styles.authorHandle, { color: c.textSecondary }]}>
                @{item.author_handle}
              </Text>
            )}
          </View>
          <Text style={[styles.timeAgo, { color: c.textTertiary }]}>{timeAgo}</Text>
        </View>

        {/* Lift info */}
        <Text style={[styles.liftTitle, { color: c.accent }]}>{item.title}</Text>
        {item.body && (
          <Text style={[styles.liftBody, { color: c.textSecondary }]}>{item.body}</Text>
        )}

        {/* Proof media — image or video */}
        {item.media_url && (
          <ProofMedia uri={item.media_url} style={styles.proofImage} />
        )}

        {/* Vote status / actions */}
        <View style={styles.voteSection}>
          <Text style={[styles.voteCount, { color: c.textSecondary }]}>
            {item.vote_count} vote{item.vote_count !== 1 ? 's' : ''}
          </Text>

          {item.user_voted ? (
            <Text style={[styles.votedLabel, { color: c.accent }]}>✓ Voted</Text>
          ) : (
            <View style={styles.voteButtons}>
              <TouchableOpacity
                style={[styles.voteBtn, styles.legitBtn, { opacity: isVoting ? 0.5 : 1 }]}
                disabled={isVoting || remaining <= 0}
                accessibilityLabel="Vote legitimate"
                accessibilityRole="button"
                onPress={() => handleVote(item.id, 'legit')}>
                <Text style={styles.voteBtnText}>✓ Legit</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.voteBtn, styles.suspiciousBtn, { opacity: isVoting ? 0.5 : 1 }]}
                disabled={isVoting || remaining <= 0}
                accessibilityLabel="Vote suspicious"
                accessibilityRole="button"
                onPress={() => handleVote(item.id, 'suspicious')}>
                <Text style={styles.voteBtnText}>✗ Suspicious</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      </View>
    );
  }, [c, votingId, remaining, handleVote]);

  return (
    <SafeAreaView edges={['top']} style={[styles.container, { backgroundColor: c.background }]}>
      <View style={styles.header}>
        <Text style={[styles.title, { color: c.accent }]}>VERIFY</Text>
        <Text style={[styles.subtitle, { color: c.textSecondary }]}>
          Community lift verification
        </Text>
        <View style={[styles.remainingBadge, { backgroundColor: c.surface, borderColor: c.border }]}>
          <Text style={[styles.remainingText, { color: remaining > 0 ? c.text : c.error }]}>
            {remaining} votes remaining today
          </Text>
        </View>
      </View>

      {loading ? (
        <SkeletonRow rows={4} />
      ) : posts.length === 0 ? (
        <EmptyStateInline title="Nothing to verify" description="No posts to verify right now. Check back later." />
      ) : (
        <FlatList
          data={posts}
          keyExtractor={(item) => item.id}
          renderItem={renderPost}
          contentContainerStyle={styles.list}
          showsVerticalScrollIndicator={false}
        />
      )}
    </SafeAreaView>
  );
}

function getTimeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (hours < 1) return 'Just now';
  if (hours === 1) return '1 hour ago';
  if (hours < 24) return `${hours} hours ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  header: { paddingHorizontal: 20, paddingTop: 16, paddingBottom: 12 },
  title: { fontSize: 24, fontWeight: '900', letterSpacing: 2 },
  subtitle: { fontSize: 14, marginTop: 2 },
  remainingBadge: {
    marginTop: 12, paddingHorizontal: 12, paddingVertical: 6,
    borderRadius: 4, borderWidth: 1, alignSelf: 'flex-start',
  },
  remainingText: { fontSize: 13, fontWeight: '600' },
  list: { paddingHorizontal: 20, paddingBottom: 40 },
  card: {
    borderWidth: 1, borderRadius: 2, padding: 16, marginBottom: 12,
  },
  cardHeader: {
    flexDirection: 'row', justifyContent: 'space-between',
    alignItems: 'flex-start', marginBottom: 8,
  },
  authorName: { fontSize: 15, fontWeight: '700' },
  authorHandle: { fontSize: 12, marginTop: 1 },
  timeAgo: { fontSize: 11 },
  liftTitle: { fontSize: 16, fontWeight: '800', letterSpacing: 1, marginBottom: 4 },
  liftBody: { fontSize: 13, marginBottom: 8 },
  proofImage: {
    width: '100%', height: 200, borderRadius: 4, marginBottom: 12,
    backgroundColor: '#1a1a1a',
  },
  voteSection: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
  },
  voteCount: { fontSize: 12 },
  votedLabel: { fontSize: 13, fontWeight: '700' },
  voteButtons: { flexDirection: 'row', gap: 8 },
  voteBtn: {
    paddingHorizontal: 14, paddingVertical: 8, borderRadius: 0,
  },
  legitBtn: { backgroundColor: '#22c55e' },
  suspiciousBtn: { backgroundColor: '#ef4444' },
  voteBtnText: { color: '#fff', fontSize: 13, fontWeight: '700' },
});
